import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { Phone, AlertCircle } from 'lucide-react';
import { maskPhone } from 'src/utils/masks';
import { useIsMobile } from '@/hooks/use-mobile';
import PhoneNumberModalMobile from './PhoneNumberModalMobile';

const PhoneNumberModal = ({ open, onSubmit }) => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formik = useFormik({
    initialValues: {
      phone: '',
    },
    validationSchema: yup.object({
      phone: yup
        .string()
        .required('O telefone é obrigatório')
        .matches(
          /^\([1-9]{2}\) (?:[2-8]|9[1-9])[0-9]{3,4}-[0-9]{4}$/,
          'Formato inválido. Use: (11) 99999-9999'
        ),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError('');
        // Remove mask characters to send only numbers
        const cleanPhone = values.phone.replace(/\D/g, '');
        await onSubmit(cleanPhone);
      } catch (err) {
        setError(err.message || 'Erro ao atualizar telefone. Tente novamente.');
        setLoading(false);
      }
    },
  });

  const handlePhoneChange = (e) => {
    const maskedValue = maskPhone(e.target.value);
    formik.setFieldValue('phone', maskedValue);
  };

  // Render mobile version
  if (isMobile) {
    return <PhoneNumberModalMobile open={open} onSubmit={onSubmit} />;
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[425px]" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Número de Telefone Obrigatório
          </DialogTitle>
          <DialogDescription>
            Para continuar usando a plataforma, precisamos do seu número de telefone para contato e
            segurança da conta. Este número será usado para comunicações importantes sobre sua conta.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={formik.handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">
              Telefone de Contato <span className="text-red-500">*</span>
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="(11) 99999-9999"
              value={formik.values.phone}
              onChange={handlePhoneChange}
              onBlur={formik.handleBlur}
              disabled={loading}
              className={formik.touched.phone && formik.errors.phone ? 'border-red-500' : ''}
              autoFocus
            />
            {formik.touched.phone && formik.errors.phone && (
              <p className="text-sm text-red-500">{formik.errors.phone}</p>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="submit"
              disabled={loading || !formik.values.phone}
              className="w-full"
            >
              {loading ? 'Salvando...' : 'Salvar e Continuar'}
            </Button>
          </DialogFooter>

          <div className="text-xs text-muted-foreground text-center">
            <p>Ao fornecer seu número, você concorda com nossos termos de uso e política de privacidade.</p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneNumberModal;